import React, {  useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Stethoscope, 
  ArrowRight, 
  CheckCircle, 
  BarChart3,
  Clock,
  Shield,
  Target,
  Eye,
  Play,
  FileText,
  Clipboard,
  UserCheck,
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';

const MedicalCodingPage: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  


  const challenges = [
    'Ensuring accurate and timely medical coding for billing and compliance',
    'Managing complex healthcare documentation and record-keeping requirements',
    'Staying current with evolving medical coding standards and regulations',
    'Reducing coding errors and improving audit readiness',
    'Integrating coding systems with existing healthcare information infrastructure',
    'Training and managing skilled medical coding professionals across multiple facilities'
  ];

  const solutions = [
    {
      name: 'Medical Coding Platform',
      path: '/products/iot#medical-coding',
      description: 'Comprehensive medical coding and billing platform designed for healthcare providers and coding professionals.',
      benefits: ['Automated Coding', 'Compliance Management', 'Audit Trail']
    }
  ];

  const keyFeatures = [
    {
      icon: FileText,
      title: 'Intelligent Coding Automation',
      description: 'AI-powered medical coding that ensures accuracy, compliance, and efficiency in healthcare billing processes.'
    },
    {
      icon: Shield,
      title: 'Compliance & Audit Ready',
      description: 'Built-in compliance monitoring and audit trail capabilities to meet healthcare regulatory requirements.'
    },
    {
      icon: UserCheck,
      title: 'Professional Training Support',
      description: 'Comprehensive training and certification support for medical coding professionals and healthcare staff.'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Reporting',
      description: 'Advanced analytics and reporting tools that provide insights into coding performance and billing efficiency.'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: 'Coding Efficiency',
      description: 'Automated coding processes and intelligent assistance significantly reduce coding time and improve throughput.',
      metric: '70% faster coding'
    },
    {
      icon: Target,
      title: 'Accuracy Improvement',
      description: 'AI-powered validation and error detection dramatically improve coding accuracy and reduce billing errors.',
      metric: '95% accuracy rate'
    },
    {
      icon: BarChart3,
      title: 'Revenue Optimization',
      description: 'Improved coding accuracy and compliance result in better reimbursement rates and reduced claim denials.',
      metric: '60% revenue increase'
    },
    {
      icon: Shield,
      title: 'Compliance Assurance',
      description: 'Automated compliance monitoring and audit trail capabilities ensure adherence to healthcare regulations.',
      metric: '100% audit ready'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 6 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-indigo-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 5,
                delay: i * 0.4,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-linear-to-br from-indigo-500 to-purple-600 neon-glow">
                  <Stethoscope className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-indigo-400 uppercase tracking-wide">Healthcare Coding Technology</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    Medical <span className="bg-linear-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">Coding</span>
                  </h1>
                </div>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-2xl font-semibold mb-4 text-indigo-300"
              >
                Revolutionizing Healthcare Billing Through Intelligent Coding
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed"
              >
                Medical coding is the backbone of healthcare billing and compliance, requiring precision, expertise, and 
                adherence to complex regulatory standards. As healthcare systems become more complex and regulations 
                evolve, providers need intelligent solutions that ensure accurate coding, improve billing efficiency, 
                and maintain compliance. At amasQIS.ai, we deliver advanced medical coding platforms that combine 
                AI-powered automation with professional expertise to streamline coding processes and optimize revenue cycles.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-indigo-500 to-purple-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-indigo-500/25 transition-all duration-300"
                >
                  Optimize Coding
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative perspective-1000"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                  className="absolute -inset-10 bg-linear-to-r from-indigo-500 to-purple-600 opacity-20 rounded-full blur-3xl"
                />
                
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                  transition={{ duration: 0.4 }}
                  className="relative z-10 glass-strong rounded-2xl overflow-hidden transform-gpu"
                >
                  <ImageWithFallback
                    src='/assets/Industries/medical-coding.png'
                    alt="Medical Coding Industry"
                    className="w-full h-96 object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-transparent to-transparent">
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center gap-3 text-white">
                        <div className="p-2 bg-indigo-500/20 rounded-lg backdrop-blur-sm">
                          <Stethoscope className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div>
                          <p className="font-semibold">AI-Powered</p>
                          <p className="text-sm text-white/80">Coding Solutions</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                  className="absolute -top-4 -right-4 glass p-3 rounded-xl"
                >
                  <div className="flex items-center gap-2 text-indigo-500">
                    <FileText className="w-4 h-4 animate-pulse" />
                    <span className="text-xs font-mono">ICD-10</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Challenges in Medical Coding
            </h2>
            <p className="text-lg text-muted-foreground  text-center max-w-3xl mx-auto">
              Healthcare providers face increasing complexity in medical coding requirements, demanding solutions that ensure accuracy and compliance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <Clipboard className="w-5 h-5 text-indigo-400 mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{challenge}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Solution for Medical Coding
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Comprehensive medical coding platform designed to enhance accuracy, ensure compliance, and optimize billing processes for healthcare providers.
            </p>
          </motion.div>

          <div className="flex justify-center">
            {solutions.map((solution, index) => (
              <Link
                key={solution.name}
                to={solution.path}
                className="block group max-w-lg"
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-indigo-400 group-hover:text-indigo-300 transition-colors">
                      {solution.name}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="text-muted-foreground mb-4">{solution.description}</p>
                  <div className="space-y-2">
                    {solution.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        <span className="text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Healthcare Providers Choose amasQIS.ai for Medical Coding
            </h2>
            <p className="text-lg text-muted-foreground text-center  max-w-3xl mx-auto">
              Our medical coding solutions combine advanced AI technology with deep healthcare industry expertise for optimal results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="glass p-6 rounded-xl hover:glass-strong transition-all duration-300 text-center"
                >
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-8 h-8 text-indigo-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Measurable Medical Coding Impact
            </h2>
            <p className="text-lg text-muted-foreground text-center max-w-3xl mx-auto">
              Transform your medical coding operations with quantifiable improvements in accuracy, efficiency, and revenue optimization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass p-8 rounded-xl hover:glass-strong transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-indigo-500/20">
                      <Icon className="w-6 h-6 text-indigo-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-3">{benefit.description}</p>
                      <div className="text-purple-400 font-semibold">{benefit.metric}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-15">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready to Transform Medical Coding?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-8">
              Join forward-thinking healthcare providers that trust amasQIS.ai to deliver accurate, compliant, and efficient medical coding solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-linear-to-r from-indigo-500 to-purple-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-indigo-500/25 transition-all duration-300"
              >
                Optimize Coding
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Explore Solutions
                <Eye className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export { MedicalCodingPage };
